import React, { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Icon from '../../../../common/components/icon';
import Image from '../../../../common/components/image';
import Dropdown from '../../../../common/components/dropdown';
import ImagePlaceholder from '../../../../common/components/image-placeholder';
import ConfirmButton from '@common/components/confirm-button';

export type Props = {
  id?: string,
  item: {
    id?: string,
    name: string,
    image?: string,
    icon?: React.ReactNode,
    member_count?: number,
    is_member?: boolean,
    membership?: {
      hidden_from_feed: boolean;
      enabled_notifications: boolean;
    },
    isSearch?: boolean,
    isGeneral?: boolean,
    networkName?: string,
  },
  isNetwork?: boolean,
  selected?: boolean,
  sub?: boolean,
  onSelectChannel: (id?: string) => void,
  onHideChannel?: (hidden: boolean) => void,
  onMute?: (muted: boolean) => void,
  addClassName?: string,
  onLeaveChannel?: () => void,
  contentWrapper?: (children: React.ReactNode) => React.ReactNode,
};

const ChannelItemComponent = ({
  id, item, selected, sub, onSelectChannel, onHideChannel, onMute, isNetwork, addClassName, onLeaveChannel, contentWrapper,
}: Props) => {
  const [isOpen, setOpen] = useState(false);
  const { t } = useTranslation();
  let className = 'Channels__Item';
  if (selected) className += ' Channels__Item--active';
  if (isOpen) className += ' Channels__Item--open';
  if (isNetwork) className += ' !tw-bg-transparent';
  if (addClassName) className += ` ${addClassName}`;

  const channelContent = (
    <>
      {item?.icon ? (
        <div className={`Channels__Item__Icon tw-p-1.5 tw-mr-4 ${isNetwork && 'tw-bg-gray-neutral-50  tw-rounded-lg'}`}>
          {item?.icon}
        </div>) : (
          <div className="Channels__Item__Image">
            {item.image && (
            <Image exclude src={item.image} alt={item.name} size={36} />
          )}
            {!item.image && sub && (
            <Icon type="hashtag_icon" />
          )}
            {!item.image && !sub && (
            <ImagePlaceholder size={36} name={item.name} />
          )}
          </div>)}
      <div className="Channels__Item__Info">
        <div>
          {item.name}
          {onHideChannel && item.membership?.hidden_from_feed && (
            <Icon
              type="visibility_off"
              className="Channels__Item__Status"
            />
          )}
          {onMute && !item.membership?.enabled_notifications && (
            <Icon
              type="notifications_off"
              className="Channels__Item__Status"
            />
          )}
        </div>
        {(item.member_count !== undefined && !item?.isSearch) && (
          <small>
            <Trans i18nKey="social:channels_item_members" values={{ count: item.member_count }} />
          </small>
        )}
        {item?.isSearch && !item.isGeneral && (
          <small className="tw-text-gray-muted tw-flex tw-items-start tw-gap-1">
            <Icon type="place" className="!tw-w-3.5 !tw-h-3.5" /> {item.networkName}
          </small>
        )}
      </div>
    </>
  );

  return (
    <div
      id={id}
      className={className}
      onClick={() => onSelectChannel(item.id)}
    >
      {contentWrapper ? contentWrapper(channelContent) : channelContent}
      <Dropdown
        alignRight
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        toggle={<Icon type="more_vert" className="Channels__Item__More" />}
      >
        {onHideChannel && (
          <Dropdown.Item onClick={() => onHideChannel(!item.membership?.hidden_from_feed)}>
            <Trans i18nKey={item.membership?.hidden_from_feed ? 'social:channels_item_unhide' : 'social:channels_item_hide'} />
          </Dropdown.Item>
        )}
        {onMute && (
          <Dropdown.Item onClick={() => item.membership && onMute(item.membership.enabled_notifications)}>
            <Trans i18nKey={
              !item.membership?.enabled_notifications ?
                'social:channels_item_unmute' : 'social:channels_item_mute'
            }
            />
          </Dropdown.Item>
        )}
        {onLeaveChannel && (
          <ConfirmButton
            title={t('organisation:leave_group_modal_title')}
            description={(
              <Trans
                i18nKey="organisation:leave_group_modal_description"
                values={{ name: item.name }}
                components={[<b />]}
              />
            )}
            confirmText={t('social:channels_item_leave')}
            onConfirm={() => onLeaveChannel()}
          >
            <Dropdown.Item
              onClick={() => onLeaveChannel()}
              className="!tw-text-red-link hover:!tw-text-red-link hover:!tw-bg-red-light"
            >
              <Trans i18nKey="social:channels_item_leave" />
            </Dropdown.Item>
          </ConfirmButton>
        )}
      </Dropdown>
    </div>
  );
};

export default memo(ChannelItemComponent);
